import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isNumber from 'lodash/isNumber';
import { hook, withValue } from '@g4/automation-hooks';
import Link from '@g4/next-furniture/Link';
import { TrackClick } from '@g4/trackster';

import { Box, Expando, useMedia, Text } from '../../../Boneyard';
import { getPrice } from '@g4/next-furniture/Price/Price';
import { useBookingPath } from '../../../../graphql';

import ARROW_ICON from '../../../../images/ic-arrow-blue.svg';

const propTypes = {
  automationHook: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  headingLink: PropTypes.shape({
    href: PropTypes.string.isRequired,
    as: PropTypes.string.isRequired,
  }),
  headingLinkLabel: PropTypes.string,
  headingLinkTrackingLabel: PropTypes.string,
  amount: PropTypes.number,
  children: PropTypes.node,
  withChildrenPadding: PropTypes.bool,
  isTripSummary: PropTypes.bool,
  transparent: PropTypes.bool,
  uppercase: PropTypes.bool,
  showStepLink: PropTypes.bool,
  showLabel: PropTypes.string,
  hasShowItem: PropTypes.bool,
  hidePrice: PropTypes.bool,
};

const ArrowIcon = styled.img.attrs({
  src: ARROW_ICON,
  alt: '',
})`
  width: 17px;
  height: 17px;
`;

const ArrowIconContainer = styled.div`
  margin-right: ${props => props.theme.spacing(1)};
  margin-left: 9px;

  transform: ${props =>
    props.isOpen
      ? 'rotate(0deg) translateY(0)'
      : 'rotate(180deg) translateY(2px)'};
  transition: 0.2s;
`;

const ExpandoLabel = styled.div`
  width: 100%;
  height: ${props => (props.hasShowItem ? '55px' : '45px')};
  background: ${props =>
    props.transparent
      ? props.theme.colors.common.light
      : props.theme.colors.cart.item.background};
  text-align: left;
  padding-left: ${props =>
    props.isMobile ? props.theme.spacing(2) : props.theme.spacing(4)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: ${props => props.uppercase && 'uppercase'};
  border-top: 1px solid
    ${props =>
    props.transparent
      ? props.theme.colors.common.light
      : props.theme.colors.cart.item.border};
  border-bottom: 1px solid
    ${props =>
    props.transparent
      ? props.theme.colors.common.light
      : props.theme.colors.cart.item.border};
`;

const EditAnchor = styled.a`
  font-size: ${props => props.theme.toRem(12)};
  text-decoration: underline;
  text-transform: uppercase;
  color: ${props => props.theme.colors.cart.item.edit};
`;

/**
 * Forward ref is required so we can use an element other than `a` as the first child of next/link:
 * https://nextjs.org/docs/api-reference/next/link#example-with-reactforwardref
 */
const HeadingLink = React.forwardRef(
  ({ trackingLabel, isTripSummary, ...props }, ref) => (
    <TrackClick
      category="site_click_tracking"
      action={isTripSummary ? 'Review and Payment' : 'Trip Summary'}
      label={trackingLabel}
    >
      <EditAnchor {...props} ref={ref} />
    </TrackClick>
  )
);

HeadingLink.propTypes = {
  children: PropTypes.node.isRequired,
  trackingLabel: PropTypes.string.isRequired,
  isTripSummary: PropTypes.bool,
};

const CartItem = ({
  showStepLink,
  automationHook,
  amount,
  label,
  showLabel,
  hasShowItem,
  headingLink,
  headingLinkLabel,
  headingLinkTrackingLabel,
  children,
  withChildrenPadding = true,
  isTripSummary,
  hidePrice,
  transparent = false,
  uppercase = true,
}) => {
  const mq = useMedia();
  const isMobile = mq.lte('md');
  const { price } = getPrice(amount);

  const [isExpanded, setIsExpanded] = React.useState(true);
  const toggleIsExpanded = () => {
    if (isTripSummary) {
      return;
    }
    setIsExpanded(prevState => !prevState);
  };
  const { isStadium } = useBookingPath();

  const childrenPaddingProps = withChildrenPadding
    ? {
      p: 3,
      pt: 2,
      pb: 2,
    }
    : {};

  const labelFragment = (
    <Box w="100%" d="flex" justifyContent="space-between">
      <Box d="flex" alignItems="center">
        <Box d="block" alignItems="center">
          {!!showLabel && (
            <Text
              size={mq.lte('sm') ? 15 : isTripSummary ? 20 : 17}
              weight={500}
              automationHook={withValue(
                automationHook,
                'allegiant-stadium-package-label'
              )}
            >
              {showLabel}
            </Text>
          )}

          <Text
            size={
              mq.lte('sm') ? 15 : isTripSummary ? (hasShowItem ? 15 : 20) : 17
            }
            weight={hasShowItem ? 300 : 500}
            automationHook={withValue(automationHook, 'heading')}
          >
            {label}
          </Text>
        </Box>

        {headingLink && showStepLink && (
          <Box d="flex" ml={2}>
            <Link href={headingLink.href} as={headingLink.as} clientRoute>
              {({ anchorProps }) => (
                <HeadingLink
                  isTripSummary
                  trackingLabel={headingLinkTrackingLabel}
                  {...anchorProps}
                  {...hook(automationHook, 'link')}
                >
                  {headingLinkLabel}
                </HeadingLink>
              )}
            </Link>
          </Box>
        )}
      </Box>

      {isTripSummary
        ? isNumber(amount) && !isStadium && (
          <Text
            automationHook={withValue(automationHook, 'value')}
            size={mq.lte('sm') ? 18 : 20}
            weight={500}
          >
            {price}
          </Text>
        )
        : isNumber(amount) && !isStadium && (
          <Text
            automationHook={withValue(automationHook, 'value')}
            size={mq.lte('sm') ? 18 : 20}
            weight={500}
          >
            {hidePrice ? '$ --.--' : price}
          </Text>
        )
      }
    </Box>
  );

  // If there is content for the cart section then it should be expandable
  if (children) {
    return (
      <Expando
        label={
          <ExpandoLabel
            uppercase={uppercase}
            transparent={transparent}
            isMobile={isMobile}
            hasShowItem={hasShowItem}
          >
            {labelFragment}

            <ArrowIconContainer isOpen={isExpanded}>
              {!isTripSummary && <ArrowIcon />}
            </ArrowIconContainer>
          </ExpandoLabel>
        }
        automationHook={automationHook}
        open={isExpanded}
        a11yLabel={label}
        onToggle={toggleIsExpanded}
        icon="NONE"
      >
        {isExpanded && (
          <Box
            {...childrenPaddingProps}
            automationHook={withValue(automationHook, 'content')}
          >
            {children}
          </Box>
        )}
      </Expando>
    );
  }

  // If there is no content for the section then we should render the label
  return (
    !isTripSummary && (
      <ExpandoLabel uppercase={uppercase} transparent={transparent}>
        {labelFragment}
      </ExpandoLabel>
    )
  );
};

CartItem.propTypes = propTypes;

export default CartItem;
